import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { isEmpty } from 'ramda';
import { Box, TextField, Button, InputAdornment, Typography } from '@material-ui/core';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import Icon from 'sharedComponents/Icon';
import { initialValues, validationEmailSchema, validationPasswordSchema } from 'forms/buyerRegistrationForm';
import useApp from 'reducers/hooks/useApp';

import useStyles from './useStyles';

const STEPS = {
  first: 'email',
  second: 'password',
};

const getErrorData = error => {
  const { errors, data } = error;
  const { email } = data;
  const message = errors.full_messages[0];
  return { email, message };
};

const SteppedBuyerRegistrationForm = props => {
  const { t } = useTranslation();
  const [isPasswordVisible, setPasswordVisible] = useState(false);
  const [currentStep, setCurrentStep] = useState(STEPS.first);
  const classes = useStyles();
  const { error: registrationError } = useApp();

  const { onSubmit } = props;

  const isEmailStep = currentStep === STEPS.first;
  const isPasswordStep = currentStep === STEPS.second;
  const currentValidationSchema = isEmailStep ? validationEmailSchema : validationPasswordSchema;

  const handleClickNext = values => {
    if (isPasswordStep) {
      onSubmit(values);
    } else {
      setCurrentStep(STEPS.second);
    }
  };

  const formik = useFormik({
    validationSchema: currentValidationSchema,
    initialValues,
    onSubmit: handleClickNext,
  });

  const {
    errors,
    values,
    handleChange,
    handleBlur,
    touched,
    handleSubmit,
    setFieldError,
    setFieldTouched,
    setFieldValue,
  } = formik;

  const isAbleToSubmit = isEmpty(errors);

  useEffect(() => {
    if (!isEmpty(registrationError)) {
      const { message, email } = getErrorData(registrationError);
      setCurrentStep('email');
      setFieldValue('email', email, false);
      setFieldError('email', message);
      setFieldTouched('email', true, false);
    }
  }, [registrationError]);

  const handleClickShowPassword = () => setPasswordVisible(!isPasswordVisible);
  return (
    <Box className={classes.paper}>
      <form className={classes.form} onSubmit={handleSubmit}>
        {isEmailStep && (
          <React.Fragment>
            <Box width="100%" display="flex" mt={3}>
              <Typography variant="body1">{t('components.SteppedBuyerRegistrationForm.header')}</Typography>
            </Box>
            <TextField
              error={touched.email && Boolean(errors.email)}
              helperText={
                (touched.email && t(errors.email)) || (
                  <span className={classes.caption}>
                    <Icon name="lock" width="16px" height="16px" style={{ marginRight: '2px' }} />
                    {t('components.SteppedBuyerRegistrationForm.caption')}
                  </span>
                )
              }
              variant="outlined"
              id="email"
              name="email"
              type="email"
              placeholder={t('components.SteppedBuyerRegistrationForm.email')}
              autoComplete="email"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.email}
              className={classes.field}
            />
          </React.Fragment>
        )}
        {isPasswordStep && (
          <React.Fragment>
            <TextField
              error={touched.password && Boolean(errors.password)}
              helperText={touched.password && t(errors.password)}
              variant="outlined"
              name="password"
              type={isPasswordVisible ? 'text' : 'password'}
              id="password"
              placeholder={t('components.SteppedBuyerRegistrationForm.password')}
              autoComplete="new-password"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.password}
              className={classes.field}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {isPasswordVisible ? (
                      <Icon name="visible" onClick={handleClickShowPassword} className={classes.visibilityIcon} />
                    ) : (
                      <Icon name="visibleOff" onClick={handleClickShowPassword} className={classes.visibilityIcon} />
                    )}
                  </InputAdornment>
                ),
              }}
            />
          </React.Fragment>
        )}
        <Button className={classes.button} type="submit" disabled={!isAbleToSubmit}>
          {t('components.SteppedBuyerRegistrationForm.button')}
        </Button>
      </form>
    </Box>
  );
};

SteppedBuyerRegistrationForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
};

export default SteppedBuyerRegistrationForm;



// WEBPACK FOOTER //
// ./src/sharedComponents/SteppedBuyerRegistrationForm/SteppedBuyerRegistrationForm.js